import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { partnerTypes, platformPartnerTypeSlug, type PartnerRole } from '@eis/core';
import { usePartnerRoles, usePmmPartnerTypes } from '../api/tarifs';
import { POLICYHOLDER_ROLE_ERN } from '../model/manifest-form';

/**
 * The manifest's fifth block: which partner roles a policy on this version may carry (SDD-011).
 *
 * A LIST of typed rules, not `key = ern` text: a role has a cardinality and a text line cannot
 * carry one honestly. The rows follow the core-config editor's idiom (ObjectRowsEditor) — one
 * outlined row per rule, remove on the right, add below — hand-written rather than
 * schema-generated because the columns are fixed by the manifest schema and known here.
 *
 * # What the operator cannot do
 *
 * Remove or loosen the policyholder. Every version lists it with minCount 1 and maxCount 1 (P-6):
 * the gate refuses anything else, so the row is locked rather than offered and then refused.
 *
 * # Where the roles come from
 *
 * The Partner domain's tag catalog (`GET /v1/partner-roles`) — the same catalog the gate vouches
 * roles against, so the picker cannot offer a role the gate then refuses. The display `name` is
 * prefilled from the catalog and stays editable: it is a SNAPSHOT copied into the manifest at
 * publish (P-5), so an approved version keeps showing what it showed when it was approved.
 */
export function PartnerRolesEditor({
  value,
  onChange,
  errors,
}: {
  readonly value: readonly PartnerRole[];
  readonly onChange: (next: PartnerRole[]) => void;
  /** Messages keyed `${row}.${attribute}` or `${row}`, from the manifest form's issues. */
  readonly errors: Readonly<Record<string, string>>;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const catalog = usePartnerRoles();
  const tags = catalog.data?.roles ?? [];
  // The partner TYPES a role may constrain to (P-10) are the Partner service's live type catalog
  // (SDD-014 PS-9) — the gate vouches a manifest's `partnerTypes` against it, so the picker offers
  // exactly what the gate accepts, in the platform's spelling (STOP-3). The two-value constant is
  // the offline default while the catalog has not answered or cannot.
  const pmmTypes = usePmmPartnerTypes();
  const typeOptions: { value: string; label: string }[] =
    pmmTypes.data === undefined
      ? partnerTypes.map((type) => ({ value: type, label: t(`tarifs.partnerRoles.type.${type}`) }))
      : [...new Map(
          pmmTypes.data.items.map((type) => [
            platformPartnerTypeSlug(type.slug),
            { value: platformPartnerTypeSlug(type.slug), label: type.name },
          ]),
        ).values()];

  const set = (index: number, patch: Partial<PartnerRole>): void => {
    onChange(value.map((role, i) => (i === index ? { ...role, ...patch } : role)));
  };

  const errorAt = (index: number, attribute?: string): string =>
    errors[attribute === undefined ? String(index) : `${String(index)}.${attribute}`] ?? '';

  const nextTag = (): ConfigApi.PartnerRoleCatalogEntry | undefined =>
    tags.find((tag) => !value.some((role) => role.roleErn === tag.ern));

  return (
    <Stack component="section" spacing={1.5} aria-labelledby="partner-roles-editor">
      <Box>
        <Typography variant="subtitle2" component="h3" id="partner-roles-editor">
          {t('tarifs.partnerRoles.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('tarifs.partnerRoles.hint')}
        </Typography>
      </Box>

      {catalog.isError && (
        <Alert severity="warning">{t('tarifs.partnerRoles.catalogUnavailable')}</Alert>
      )}

      <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {value.map((role, index) => {
          const locked = role.roleErn === POLICYHOLDER_ROLE_ERN;
          const known = tags.some((tag) => tag.ern === role.roleErn);
          return (
            <Paper
              key={index}
              component="li"
              variant="outlined"
              sx={{ p: 1.5 }}
              data-pointer={`/partnerRoles/${String(index)}`}
              data-testid={locked ? 'partner-role-policyholder' : undefined}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    flex: 1,
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  }}
                >
                  <TextField
                    select={known || role.roleErn === ''}
                    size="small"
                    label={t('tarifs.partnerRoles.role')}
                    value={role.roleErn}
                    disabled={locked}
                    onChange={(event) => {
                      const tag = tags.find((candidate) => candidate.ern === event.target.value);
                      set(index, {
                        roleErn: event.target.value,
                        // Prefilled from the catalog, editable afterwards (P-5).
                        name: tag?.name ?? role.name,
                      });
                    }}
                    error={errorAt(index, 'roleErn') !== ''}
                    helperText={errorAt(index, 'roleErn') || t('tarifs.partnerRoles.roleHint')}
                  >
                    {(known || role.roleErn === '') &&
                      tags.map((tag) => (
                        <MenuItem key={tag.ern ?? ''} value={tag.ern ?? ''}>
                          {tag.name} ·{' '}
                          <Box component="code" sx={{ ml: 0.5 }}>
                            {tag.ern}
                          </Box>
                        </MenuItem>
                      ))}
                  </TextField>
                  <TextField
                    size="small"
                    label={t('tarifs.partnerRoles.name')}
                    value={role.name}
                    onChange={(event) => {
                      set(index, { name: event.target.value });
                    }}
                    error={errorAt(index, 'name') !== ''}
                    helperText={errorAt(index, 'name') || t('tarifs.partnerRoles.nameHint')}
                  />
                  <Stack direction="row" spacing={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={role.isRequired}
                          disabled={locked}
                          onChange={(event) => {
                            const isRequired = event.target.checked;
                            set(index, {
                              isRequired,
                              // isRequired and minCount are one truth (P-2): keep them agreeing.
                              minCount: isRequired ? Math.max(role.minCount, 1) : 0,
                            });
                          }}
                        />
                      }
                      label={t('tarifs.partnerRoles.isRequired')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={role.isMulti}
                          disabled={locked}
                          onChange={(event) => {
                            const isMulti = event.target.checked;
                            set(index, {
                              isMulti,
                              // Not multi ⇒ bounded by 1; multi ⇒ the bound is the operator's.
                              ...(isMulti ? {} : { maxCount: 1 }),
                            });
                          }}
                        />
                      }
                      label={t('tarifs.partnerRoles.isMulti')}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      type="number"
                      label={t('tarifs.partnerRoles.minCount')}
                      value={role.minCount}
                      disabled={locked}
                      onChange={(event) => {
                        const minCount = Number(event.target.value);
                        set(index, { minCount, isRequired: minCount > 0 });
                      }}
                      slotProps={{ htmlInput: { min: 0 } }}
                      error={errorAt(index, 'minCount') !== '' || errorAt(index) !== ''}
                      helperText={errorAt(index, 'minCount') || errorAt(index)}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t('tarifs.partnerRoles.maxCount')}
                      value={role.maxCount ?? ''}
                      disabled={locked || !role.isMulti}
                      onChange={(event) => {
                        const raw = event.target.value;
                        set(index, { maxCount: raw === '' ? undefined : Number(raw) });
                      }}
                      slotProps={{ htmlInput: { min: 1 } }}
                      error={errorAt(index, 'maxCount') !== ''}
                      helperText={errorAt(index, 'maxCount') || t('tarifs.partnerRoles.unbounded')}
                    />
                  </Stack>
                  <TextField
                    select
                    size="small"
                    label={t('tarifs.partnerRoles.types')}
                    value={role.partnerTypes ?? []}
                    onChange={(event) => {
                      const selected = event.target.value as unknown as string[];
                      set(index, { partnerTypes: selected.length === 0 ? undefined : selected });
                    }}
                    slotProps={{ select: { multiple: true } }}
                    helperText={
                      errorAt(index, 'partnerTypes') ||
                      (pmmTypes.isError ? t('tarifs.partnerRoles.typesOffline') : t('tarifs.partnerRoles.typesHint'))
                    }
                    error={errorAt(index, 'partnerTypes') !== ''}
                    data-testid="partner-role-types"
                  >
                    {typeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                {locked ? (
                  <Tooltip title={t('tarifs.partnerRoles.locked')}>
                    <LockOutlinedIcon
                      fontSize="small"
                      color="action"
                      aria-label={t('tarifs.partnerRoles.locked')}
                    />
                  </Tooltip>
                ) : (
                  <IconButton
                    size="small"
                    aria-label={t('tarifs.partnerRoles.remove', { position: index + 1 })}
                    onClick={() => {
                      onChange(value.filter((_, i) => i !== index));
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            const tag = nextTag();
            onChange([
              ...value,
              {
                roleErn: tag?.ern ?? '',
                name: tag?.name ?? '',
                isRequired: false,
                isMulti: false,
                minCount: 0,
                maxCount: 1,
              },
            ]);
          }}
        >
          {t('tarifs.partnerRoles.add')}
        </Button>
      </Box>
    </Stack>
  );
}
